import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Wallet, AlertCircle, BellRing, MessageCircle, FileSpreadsheet, CalendarDays, Send } from "lucide-react";
import { API, auth, handleAuthError, ghostBtn, fmtDate } from "./shared";

const fmt = (v) => `${Math.round(v || 0).toLocaleString("fr-FR")} FCFA`;
const dayLabel = (d) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;

const AdminPayments = ({ token, logout }) => {
  const [p, setP] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState("");

  const exportAccounting = async () => {
    setBusy("export");
    try {
      const res = await axios.get(`${API}/export/accounting`, { ...auth(token), params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `comptabilite-${month}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Export comptable ${month} téléchargé`);
    } catch (err) {
      handleAuthError(err, logout, () => toast.error("Export impossible"));
    } finally { setBusy(""); }
  };
  const weekSummary = async () => {
    setBusy("week");
    try {
      const r = await axios.get(`${API}/reports/week-summary`, auth(token));
      window.open(r.data.url, "_blank", "noopener");
      toast.success(`Carnet de la semaine prêt dans WhatsApp : ${r.data.rdv} RDV, ${r.data.unpaid} impayé(s)`);
    } catch (err) {
      handleAuthError(err, logout, () => toast.error("Résumé indisponible"));
    } finally { setBusy(""); }
  };
  const weekSummarySend = async () => {
    setBusy("weeksend");
    try {
      const r = await axios.post(`${API}/reports/week-summary/send`, {}, auth(token));
      toast.success(r.data.email || r.data.whatsapp ? `Carnet envoyé${r.data.email ? " par e-mail au directeur" : ""}${r.data.whatsapp ? " et par WhatsApp" : ""}` : "Aucun canal configuré (e-mail directeur / Twilio)");
    } catch (err) {
      handleAuthError(err, logout, () => toast.error("Envoi impossible"));
    } finally { setBusy(""); }
  };
  const load = () => axios.get(`${API}/payments/summary`, auth(token)).then((r) => setP(r.data)).catch((err) => handleAuthError(err, logout, () => {}));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const remind = async (u) => {
    try {
      const r = await axios.post(`${API}/quotes/${u.quote_id}/invoice/remind`, {}, auth(token));
      if (r.data.whatsapp_url) window.open(r.data.whatsapp_url, "_blank", "noopener");
      toast.success(`Relance ${r.data.emailed ? "envoyée par e-mail" : ""}${r.data.emailed && r.data.whatsapp_url ? " et " : ""}${r.data.whatsapp_url ? "préparée sur WhatsApp" : ""} · ${u.name}`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      handleAuthError(err, logout, () => toast.error(typeof d === "string" ? d : "Relance impossible"));
    }
  };

  if (!p) return null;
  const maxDay = Math.max(1, ...p.by_day.map((d) => d.total));
  const maxMethod = Math.max(1, ...p.by_method.map((m) => m.total));

  return (
    <div className="mt-10 space-y-6" data-testid="stat-payments">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Wallet className="h-5 w-5 text-gold" />
          <h2 className="font-display text-lg font-bold text-forest">Journal des encaissements</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="pay-actions">
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="pay-export-month" className="rounded-full border border-brand/20 bg-white px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-forest outline-none focus:border-gold" />
          <button onClick={exportAccounting} disabled={busy === "export"} data-testid="pay-export-accounting" className="inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-brand-hover disabled:opacity-60" title="Factures, encaissements et récapitulatif du mois pour votre comptable"><FileSpreadsheet className="h-3.5 w-3.5" /> Export comptable</button>
          <button onClick={weekSummary} disabled={busy === "week"} data-testid="pay-week-summary" className={ghostBtn} title="Résumé WhatsApp des rendez-vous et impayés de la semaine (envoyé automatiquement chaque lundi)"><CalendarDays className="h-3.5 w-3.5" /> Carnet de la semaine (WhatsApp)</button>
          <button onClick={weekSummarySend} disabled={busy === "weeksend"} data-testid="pay-week-summary-send" className={`${ghostBtn} px-3`} title="Envoyer maintenant le carnet au directeur (e-mail, et WhatsApp si Twilio configuré)"><Send className="h-3.5 w-3.5" /> Envoyer</button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-4">
        {[["Encaissé aujourd'hui", fmt(p.today), "pay-today"], ["Encaissé ce mois", `${fmt(p.month_total)}`, "pay-month"], ["Total encaissé", fmt(p.total_paid), "pay-total"], ["Reste à encaisser", `${fmt(p.outstanding_total)} · ${p.outstanding_count} facture${p.outstanding_count > 1 ? "s" : ""}`, "pay-outstanding"]].map(([label, value, id], i) => (
          <div key={id} className={`rounded-2xl p-5 ${i === 3 ? "bg-cream border border-gold/30" : "border border-brand/10 bg-white"}`} data-testid={`stat-${id}`}>
            <p className="font-mono text-[10px] uppercase tracking-widest text-brand">{label}</p>
            <p className={`font-display mt-2 text-xl font-bold ${i === 3 ? "text-gold" : "text-forest"}`}>{value}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <div className="rounded-3xl bg-mint p-8 lg:col-span-3" data-testid="stat-pay-by-day">
          <h3 className="font-display text-base font-bold text-forest">Encaissements par jour</h3>
          <p className="mt-1 text-xs text-brand">30 derniers jours</p>
          <div className="mt-6 flex h-40 items-end gap-1">
            {p.by_day.map((d, i) => (
              <div key={d.date} className="group relative flex flex-1 flex-col items-center justify-end" title={`${dayLabel(d.date)} : ${fmt(d.total)} (${d.count})`} data-testid={`stat-pay-day-${i}`}>
                <div className={`w-full rounded-t-md transition-all duration-700 ${d.total > 0 ? "bg-brand group-hover:bg-gold" : "bg-white/60"}`} style={{ height: `${Math.max(3, (d.total / maxDay) * 130)}px` }} />
                {i % 5 === 0 && <span className="mt-1 font-mono text-[8px] uppercase text-brand">{dayLabel(d.date)}</span>}
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-3xl border border-brand/10 bg-white p-8 lg:col-span-2" data-testid="stat-pay-by-method">
          <h3 className="font-display text-base font-bold text-forest">Par moyen de paiement</h3>
          <ul className="mt-6 space-y-4">
            {p.by_method.map((m, i) => (
              <li key={m.method} data-testid={`stat-pay-method-${m.method}`}>
                <div className="flex items-center justify-between gap-3 text-sm"><span className="font-semibold text-forest">{m.label}</span><span className="font-mono text-xs text-brand">{fmt(m.total)} · {m.count}</span></div>
                <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-mint"><div className={`h-full rounded-full ${i === 0 ? "bg-gold" : "bg-brand"}`} style={{ width: `${(m.total / maxMethod) * 100}%` }} /></div>
              </li>
            ))}
            {p.by_method.length === 0 && <li className="text-sm text-brand">Aucun encaissement enregistré : utilisez « Paiement reçu » sur une facture.</li>}
          </ul>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-3xl border border-gold/30 bg-cream p-8" data-testid="stat-unpaid">
          <h3 className="font-display flex items-center gap-2 text-base font-bold text-forest"><AlertCircle className="h-4 w-4 text-gold" /> Factures impayées ({p.unpaid.length})</h3>
          <p className="mt-1 text-xs text-brand-hover">Rappel automatique par e-mail après le délai réglé dans Paramètres ; le bouton envoie l'e-mail et prépare le WhatsApp.</p>
          <ul className="mt-5 space-y-3">
            {p.unpaid.map((u, i) => (
              <li key={u.quote_id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white p-4" data-testid={`stat-unpaid-${i}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-forest">{u.name} · {u.number}</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-brand">{fmt(u.amount)} · émise le {fmtDate(u.created_at)}{u.reminders ? ` · ${u.reminders} rappel${u.reminders > 1 ? "s" : ""} (dernier ${fmtDate(u.last_reminder)})` : ""}</p>
                </div>
                <button onClick={() => remind(u)} data-testid={`stat-unpaid-${i}-remind`} className={`${ghostBtn} px-3 py-1.5`}>{u.phone ? <MessageCircle className="h-3 w-3" /> : <BellRing className="h-3 w-3" />} Relancer</button>
              </li>
            ))}
            {p.unpaid.length === 0 && <li className="text-sm text-forest">Toutes les factures sont réglées.</li>}
          </ul>
        </div>
        <div className="rounded-3xl border border-brand/10 bg-white p-8" data-testid="stat-pay-recent">
          <h3 className="font-display text-base font-bold text-forest">Derniers paiements reçus</h3>
          <ul className="mt-5 divide-y divide-brand/10">
            {p.recent.map((r, i) => (
              <li key={`${r.quote_id}-${r.date}`} className="flex items-center justify-between gap-3 py-3 text-sm" data-testid={`stat-pay-recent-${i}`}>
                <div className="min-w-0"><p className="truncate font-semibold text-forest">{r.name} · {r.number}</p><p className="font-mono text-[10px] uppercase tracking-widest text-brand">{fmtDate(r.date)} · {r.label}{r.reference ? ` · réf. ${r.reference}` : ""}</p></div>
                <span className="shrink-0 font-semibold text-forest">{fmt(r.amount)}</span>
              </li>
            ))}
            {p.recent.length === 0 && <li className="py-3 text-sm text-brand">Aucun paiement enregistré.</li>}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminPayments;
